import {isCallClosed} from '@/utils/callPresentation';
import ModalPortal from '@/components/ModalPortal';
import { useNavigate, useParams } from 'react-router-dom';
import { CallDetails } from '@/types/call';
import { CALL_STATUS_LABELS } from '@/utils/callPresentation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertCircle, Building2, CheckCircle2, ChevronRight, ClipboardList, FileText,
    MapPin, Monitor, Paperclip, Plus, RefreshCw, Search,
    Send, User, UserCog, X, Flag,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import api from '@/api/axios';
import { useAuth } from '@/auth/AuthContext';
import {
    Applicant, Category, Equipment, Local, SubCategory, Unit, UserEntity,
} from '@/types/types';
import { EMAIL_INPUT_PATTERN, formatPhone, isValidEmail, isValidPhoneFormat } from '@/utils/formValidation';
import { CallPriority, PRIORITY_LABELS } from '@/utils/callPriority';
import { CallStatus } from '@/types/call';

const fieldClass = 'form-field min-h-12 text-sm';
const labelClass = 'ml-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400';
const getErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || error?.response?.data?.detail || fallback;

interface SectionProps {
    number: number;
    title: string;
    description: string;
    icon: React.ReactNode;
    delay?: number;
    children: React.ReactNode;
}

/** Bloco visual reutilizável para manter título, espaçamento e animação uniformes. */
const FormSection = ({ number, title, description, icon, delay = 0, children }: SectionProps) => (
    <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay }}
        className={`ui-card ui-card-hover group overflow-visible ${number === 1 ? 'z-30' : 'z-0'}`}
    >
        <div className="pointer-events-none absolute right-0 top-0 h-36 w-36 rounded-full bg-blue-500/5 blur-3xl" />
        <header className="mb-5 flex items-start gap-3 border-b border-white/5 pb-4">
            <div className="rounded-xl bg-blue-500/10 p-2 text-blue-400">{icon}</div>
            <div>
                <h2 className="text-xs font-black uppercase tracking-wider text-white">{number}. {title}</h2>
                <p className="mt-1 text-[11px] text-slate-500">{description}</p>
            </div>
        </header>
        {children}
    </motion.section>
);

export const CallPage: React.FC = () => {
    const { user, activeSecretary, setActiveSecretary } = useAuth();
    const navigate = useNavigate(); const { id } = useParams<{id:string}>();
    const [editReady,setEditReady] = useState(false);
    const preferredEquipmentId = useRef(''); const preferredSubcategoryId = useRef('');
    const userSecretaries = useMemo(() => user?.secretaries ?? [], [user]);
    const preferredLocationId = useRef('');

    const [units, setUnits] = useState<Unit[]>([]);
    const [locations, setLocations] = useState<Local[]>([]);
    const [equipments, setEquipments] = useState<Equipment[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [technicians, setTechnicians] = useState<UserEntity[]>([]);

    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
    const [responsibleTechnicianId, setResponsibleTechnicianId] = useState(user?.id || '');

    const [status, setStatus] = useState<CallStatus>('OPENED');
    const [priority, setPriority] = useState<CallPriority>('NORMAL');
    const [description, setDescription] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);

    const [applicantSearch, setApplicantSearch] = useState('');
    const [applicantResults, setApplicantResults] = useState<Applicant[]>([]);
    const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
    const [searchingApplicants, setSearchingApplicants] = useState(false);
    const [showQuickCreate, setShowQuickCreate] = useState(false);
    const [quickName, setQuickName] = useState('');
    const [quickEmail, setQuickEmail] = useState('');
    const [quickPhone, setQuickPhone] = useState('');
    const [quickUnitId, setQuickUnitId] = useState('');
    const [quickLocationId, setQuickLocationId] = useState('');
    const [quickLocations, setQuickLocations] = useState<Local[]>([]);
    const [creatingApplicant, setCreatingApplicant] = useState(false);

    const [loading, setLoading] = useState(true);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [loadingEquipments, setLoadingEquipments] = useState(false);
    const [loadingSubCategories, setLoadingSubCategories] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

    const selectedCategory = categories.find(item => item.id === selectedCategoryId);
    useEffect(() => {
        if (!activeSecretary && userSecretaries.length > 0) setActiveSecretary(userSecretaries[0]);
    }, [activeSecretary, setActiveSecretary, userSecretaries]);

    const loadInitialData = useCallback(async () => {
        setLoading(true);
        try {
            const [categoryResponse, technicianResponse, existing] = await Promise.all([
                api.get('/category/all', { params: { size: 100 } }),
                api.get('/users/technicians'),
                id ? api.get<CallDetails>(`/calls/${id}`) : Promise.resolve(null),
            ]);
            setCategories(categoryResponse.data.content ?? categoryResponse.data ?? []);
            setTechnicians(technicianResponse.data ?? []);
            if(existing){
                const c=existing.data;
                if(isCallClosed(c.status)){navigate(`/tickets/${c.id}`,{replace:true});return;}
                preferredLocationId.current=c.local?.id || '';
                preferredEquipmentId.current=c.equipment?.id || '';
                preferredSubcategoryId.current=c.subcategory?.id || '';
                setSelectedApplicant(c.applicant as Applicant); setApplicantSearch(c.applicant?.name || '');
                setSelectedUnitId(c.unit?.id || '');setSelectedCategoryId(c.subcategory?.category?.id || '');
                setResponsibleTechnicianId(c.responsibleTechnician?.id || '');
                setDescription(c.description);setStatus(c.status);setPriority(c.priority);setEditReady(true);
            }
        } catch (error) {
            setMessage({ type: 'error', text: getErrorMessage(error, 'Não foi possível carregar o formulário.') });
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { loadInitialData(); }, [loadInitialData]);

    useEffect(() => {
        if (!activeSecretary?.id) return;
        let mounted = true;
        setLoadingUnits(true);
        setSelectedApplicant(null);
        setApplicantSearch('');
        setSelectedUnitId('');
        setSelectedLocationId('');
        setSelectedEquipmentId('');
        api.get(`/unit/secretary/${activeSecretary.id}`)
            .then(response => mounted && setUnits(response.data.content ?? response.data ?? []))
            .catch(error => mounted && setMessage({ type: 'error', text: getErrorMessage(error, 'Erro ao carregar unidades.') }))
            .finally(() => mounted && setLoadingUnits(false));
        return () => { mounted = false; };
    }, [activeSecretary?.id]);

    useEffect(() => {
        if (!selectedUnitId) {
            setLocations([]);
            setSelectedLocationId('');
            return;
        }
        let mounted = true;
        setLoadingLocations(true);
        api.get(`/local/unit/${selectedUnitId}`)
            .then(response => {
                if (!mounted) return;
                const data = response.data.content ?? response.data ?? [];
                setLocations(data);
                const preferred = preferredLocationId.current;
                setSelectedLocationId(preferred && data.some((item: Local) => item.id === preferred) ? preferred : '');
                preferredLocationId.current = '';
            })
            .catch(error => mounted && setMessage({ type: 'error', text: getErrorMessage(error, 'Erro ao carregar locais.') }))
            .finally(() => mounted && setLoadingLocations(false));
        return () => { mounted = false; };
    }, [selectedUnitId]);

    useEffect(() => {
        setSelectedEquipmentId('');
        if (!selectedLocationId) {
            setEquipments([]);
            return;
        }
        let mounted = true;
        setLoadingEquipments(true);
        api.get(`/equipment/local/${selectedLocationId}`)
            .then(response => {if(!mounted)return; const data=response.data.content ?? response.data ?? [];setEquipments(data);setSelectedEquipmentId(preferredEquipmentId.current);preferredEquipmentId.current='';})
            .catch(() => mounted && setEquipments([]))
            .finally(() => mounted && setLoadingEquipments(false));
        return () => { mounted = false; };
    }, [selectedLocationId]);

    useEffect(() => {
        setSelectedSubCategoryId('');
        if (!selectedCategoryId) {
            setSubCategories([]);
            return;
        }
        let mounted = true;
        setLoadingSubCategories(true);
        api.get(`/sub-category/category/${selectedCategoryId}`)
            .then(response => {if(!mounted)return;setSubCategories(response.status === 204 ? [] : response.data.content ?? response.data ?? []);setSelectedSubCategoryId(preferredSubcategoryId.current);preferredSubcategoryId.current='';})
            .catch(() => mounted && setSubCategories([]))
            .finally(() => mounted && setLoadingSubCategories(false));
        return () => { mounted = false; };
    }, [selectedCategoryId]);

    useEffect(() => {
        if (selectedApplicant || applicantSearch.trim().length < 2) {
            setApplicantResults([]);
            return;
        }
        const timer = window.setTimeout(async () => {
            setSearchingApplicants(true);
            try {
                const response = await api.get('/applicants/search', { params: { q: applicantSearch.trim() } });
                setApplicantResults(response.data ?? []);
            } catch {
                setApplicantResults([]);
            } finally {
                setSearchingApplicants(false);
            }
        }, 300);
        return () => window.clearTimeout(timer);
    }, [applicantSearch, selectedApplicant]);

    useEffect(() => {
        if (!quickUnitId) {
            setQuickLocations([]);
            setQuickLocationId('');
            return;
        }
        api.get(`/local/unit/${quickUnitId}`).then(response => {
            const data = response.data.content ?? response.data ?? [];
            setQuickLocations(data);
            setQuickLocationId(current => data.some((item: Local) => item.id === current) ? current : '');
        }).catch(() => setQuickLocations([]));
    }, [quickUnitId]);

    const selectApplicant = (applicant: Applicant) => {
        setSelectedApplicant(applicant);
        setApplicantSearch(applicant.name);
        setApplicantResults([]);
        if (selectedUnitId === applicant.unitId) setSelectedLocationId(applicant.localId);
        else {
            preferredLocationId.current = applicant.localId;
            setSelectedUnitId(applicant.unitId);
        }
    };

    const openQuickCreate = () => {
        setQuickName(applicantSearch.trim());
        setQuickEmail('');
        setQuickPhone('');
        setQuickUnitId(selectedUnitId);
        setQuickLocationId(selectedLocationId);
        setShowQuickCreate(true);
    };

    const createApplicant = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!quickName.trim() || !quickLocationId) {
            setMessage({ type: 'error', text: 'Informe nome e local de lotação do servidor.' });
            return;
        }
        if (quickEmail && !isValidEmail(quickEmail)) {
            setMessage({ type: 'error', text: 'Informe um e-mail válido.' });
            return;
        }
        if (quickPhone && !isValidPhoneFormat(quickPhone)) {
            setMessage({ type: 'error', text: 'Use (99)9999-9999 ou (99)99999-9999.' });
            return;
        }
        setCreatingApplicant(true);
        try {
            const response = await api.post<Applicant>('/applicants', {
                name: quickName.trim(), email: quickEmail.trim() || null,
                phone: quickPhone || null, localId: quickLocationId,
            });
            selectApplicant(response.data);
            setShowQuickCreate(false);
            setMessage({ type: 'success', text: 'Servidor cadastrado e selecionado.' });
        } catch (error) {
            setMessage({ type: 'error', text: getErrorMessage(error, 'Não foi possível cadastrar o servidor.') });
        } finally {
            setCreatingApplicant(false);
        }
    };

    const createCall = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedApplicant) return setMessage({ type: 'error', text: 'Selecione um servidor cadastrado.' });
        if (!selectedUnitId || !selectedLocationId) return setMessage({ type: 'error', text: 'Informe o local do atendimento.' });
        if (!selectedCategoryId || !selectedSubCategoryId) return setMessage({ type: 'error', text: 'Complete a classificação do chamado.' });
        if (description.trim().length < 10) return setMessage({ type: 'error', text: 'Descreva o relato com pelo menos 10 caracteres.' });

        setSubmitting(true);
        setMessage(null);
        try {
            const payload = {
                description: description.trim(), applicantId: selectedApplicant.id,
                unitId: selectedUnitId, localId: selectedLocationId,
                equipmentId: selectedEquipmentId || null, categoryId: selectedCategoryId,
                subcategoryId: selectedSubCategoryId,
                responsibleTechnicianId: responsibleTechnicianId || null,
                status, priority,
            };
            const response = id ? await api.put<{id:string}>(`/calls/${id}`,payload) : await api.post<{id:string}>('/calls',payload);
            let attachmentWarning = '';
            if (attachments.length > 0) {
                const formData = new FormData();
                attachments.forEach(file => formData.append('files', file));
                try {
                    await api.post(`/calls/${response.data.id}/attachments`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                } catch {
                    attachmentWarning = ' O chamado foi criado, mas um ou mais anexos não foram enviados.';
                }
            }
            navigate('/tickets', {state:{notice:attachmentWarning || (id ? 'Chamado atualizado.' : 'Chamado registrado.')}});
        } catch (error) {
            setMessage({ type: 'error', text: getErrorMessage(error, 'Não foi possível registrar o chamado.') });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="page-content flex min-h-[50vh] items-center justify-center text-blue-400"><RefreshCw className="animate-spin" /></div>;

    return (
        <div className="page-content">
            <div className="mx-auto max-w-6xl space-y-6">
                <div className="flex items-center justify-between"><h1 className="text-xl font-bold">{id ? 'Editar chamado' : 'Novo chamado'}</h1><button type="button" className="ui-button-secondary" onClick={()=>navigate('/tickets')}>Voltar aos chamados</button></div>
                <AnimatePresence mode="wait">
                    {message && (
                        <motion.div key={message.text} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-xs font-bold ${message.type === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : message.type === 'warning' ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
                            {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                            <span className="flex-1">{message.text}</span>
                            <button type="button" onClick={() => setMessage(null)} aria-label="Fechar aviso"><X size={16} /></button>
                        </motion.div>
                    )}
                </AnimatePresence>

                <form onSubmit={createCall} className="space-y-6">
                    <FormSection number={1} title="Solicitante" description="Localize o servidor municipal que entrou em contato com o suporte." icon={<User size={18} />}>
                        <div className="relative space-y-1.5">
                                <label className={labelClass}><User size={12} /> Servidor solicitante *</label>
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={15} />
                                    <input className={`${fieldClass} pl-11 pr-11`} value={applicantSearch}
                                        onChange={event => { setApplicantSearch(event.target.value); setSelectedApplicant(null); }}
                                        placeholder="Digite ao menos 2 letras para buscar" autoComplete="off" />
                                    {searchingApplicants && <RefreshCw className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-blue-400" size={15} />}
                                </div>
                                {applicantResults.length > 0 && (
                                    <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-2xl border border-white/10 bg-[#111827] p-1 shadow-2xl">
                                        {applicantResults.map(applicant => (
                                            <button key={applicant.id} type="button" onClick={() => selectApplicant(applicant)} className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left hover:bg-white/5">
                                                <span><strong className="block text-xs text-white">{applicant.name}</strong><small className="text-[10px] text-slate-500">{applicant.unitName} · {applicant.localName}</small></span>
                                                <ChevronRight size={15} className="text-slate-600" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {!selectedApplicant && applicantSearch.trim().length >= 2 && !searchingApplicants && applicantResults.length === 0 && (
                                    <button type="button" onClick={openQuickCreate} className="mt-2 flex items-center gap-2 text-[11px] font-bold text-blue-400 hover:text-blue-300"><Plus size={14} /> Servidor não encontrado? Fazer cadastro rápido</button>
                                )}
                        </div>
                        {selectedApplicant && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-slate-300">
                                <strong className="text-emerald-300">{selectedApplicant.name}</strong>
                                <span className="ml-2 text-slate-500">{selectedApplicant.email || selectedApplicant.phone || 'sem contato cadastrado'} · Lotação: {selectedApplicant.unitName} / {selectedApplicant.localName}</span>
                            </motion.div>
                        )}
                    </FormSection>

                    <FormSection number={2} title="Local do atendimento" description="A lotação é preenchida pelo servidor, mas pode ser alterada quando o problema estiver em outro local." icon={<MapPin size={18} />} delay={0.05}>
                        <div className="grid gap-4 md:grid-cols-3">
                            <div className="space-y-1.5"><label className={labelClass}><Building2 size={12} /> Secretaria</label>
                                {userSecretaries.length > 1 ? <select className={fieldClass} value={activeSecretary?.id ?? ''} onChange={event => { const secretary = userSecretaries.find(item => item.id === event.target.value); if (secretary) setActiveSecretary(secretary); }}>{userSecretaries.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
                                    : <div className={`${fieldClass} text-slate-400`}>{activeSecretary?.name || 'Nenhuma secretaria ativa'}</div>}
                            </div>
                            <div className="space-y-1.5"><label className={labelClass}>Unidade / prédio *</label><select className={fieldClass} value={selectedUnitId} disabled={loadingUnits} onChange={event => setSelectedUnitId(event.target.value)}><option value="">Selecione</option>{units.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                            <div className="space-y-1.5"><label className={labelClass}>Local / setor *</label><select className={fieldClass} value={selectedLocationId} disabled={!selectedUnitId || loadingLocations} onChange={event => setSelectedLocationId(event.target.value)}><option value="">{loadingLocations ? 'Carregando...' : 'Selecione'}</option>{locations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                        </div>
                    </FormSection>

                    <FormSection number={3} title="Classificação" description="Escolha a área afetada e o sintoma para facilitar relatórios e encaminhamento." icon={<ClipboardList size={18} />} delay={0.1}>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1.5"><label className={labelClass}>Categoria *</label><select className={fieldClass} value={selectedCategoryId} onChange={event => setSelectedCategoryId(event.target.value)}><option value="">Selecione</option>{categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                            <div className="space-y-1.5"><label className={labelClass}>Sintoma / serviço *</label><select className={fieldClass} value={selectedSubCategoryId} disabled={!selectedCategoryId || loadingSubCategories} onChange={event => setSelectedSubCategoryId(event.target.value)}><option value="">{loadingSubCategories ? 'Carregando...' : 'Selecione'}</option>{subCategories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                        </div>
                        <AnimatePresence>
                            {selectedCategory?.equipmentRelevant && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mt-4 space-y-1.5">
                                    <label className={labelClass}><Monitor size={12} /> Equipamento relacionado (opcional)</label>
                                    <select className={fieldClass} value={selectedEquipmentId} disabled={!selectedLocationId || loadingEquipments} onChange={event => setSelectedEquipmentId(event.target.value)}>
                                        <option value="">{loadingEquipments ? 'Carregando...' : 'Nenhum equipamento específico'}</option>
                                        {equipments.map(item => <option key={item.id} value={item.id}>{item.model?.equipmentType?.name || 'Equipamento'} · {item.model?.name || 'sem modelo'} · Patrimônio {item.patrimony || 'N/I'}</option>)}
                                    </select>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </FormSection>

                    <FormSection number={4} title="Relato e encaminhamento" description="Registre o problema informado e quem ficará responsável pelo atendimento." icon={<FileText size={18} />} delay={0.15}>
                        <div className="space-y-4">
                            <div className="space-y-1.5"><label className={labelClass}>Descrição detalhada *</label><textarea className={`${fieldClass} min-h-32 resize-y`} maxLength={1000} value={description} onChange={event => setDescription(event.target.value)} placeholder="Ex.: Desde 9h o sistema de protocolo exibe erro ao salvar. Afeta todo o setor, em três computadores, e não há alternativa de trabalho." /><div className="text-right text-[9px] text-slate-700">{description.length}/1000</div></div>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1.5"><label className={labelClass}><UserCog size={12} /> Técnico responsável (opcional)</label><select className={fieldClass} value={responsibleTechnicianId} onChange={event => setResponsibleTechnicianId(event.target.value)}><option value="">Deixar para triagem</option>{technicians.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select><p className="px-1 text-[10px] text-slate-600">Registrado por {user?.name}. O responsável pode ser outra pessoa.</p></div>
                            </div>
                            <div className="space-y-1.5"><label className={labelClass}><Paperclip size={12} /> Anexos (opcional)</label><input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.txt,.doc,.docx,.xls,.xlsx" className={`${fieldClass} file:mr-3 file:rounded-lg file:border-0 file:bg-blue-500/10 file:px-3 file:py-1.5 file:text-[10px] file:font-bold file:text-blue-300`} onChange={event => setAttachments(Array.from(event.target.files ?? []))} /><p className="px-1 text-[10px] text-slate-600">PDF, imagem, texto, Word ou Excel. Até 10 MB por arquivo.</p></div>
                        </div>
                    </FormSection>

                    <FormSection number={5} title="Status e prioridade" description="Finalize o registro definindo a situação inicial e a ordem de atendimento." icon={<Flag size={18} />} delay={0.2}>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1.5">
                                <label className={labelClass}>Status *</label>
                                <select className={fieldClass} value={status} onChange={event => setStatus(event.target.value as CallStatus)}>
                                    {id && !['OPENED','IN_PROGRESS','CLOSED'].includes(status) && <option value={status}>{CALL_STATUS_LABELS[status]} (atual)</option>}
                                    <option value="OPENED">Aberto (padrão)</option>
                                    <option value="IN_PROGRESS">Em andamento</option>
                                    <option value="CLOSED">Fechado</option>
                                </select>
                                <p className="px-1 text-[10px] text-slate-600">Use “Fechado” somente quando o atendimento já tiver sido concluído durante o primeiro contato.</p>
                            </div>
                            <div className="space-y-1.5">
                                <label className={labelClass}>Prioridade *</label>
                                <select className={fieldClass} value={priority} onChange={event => setPriority(event.target.value as CallPriority)}>
                                    <option value="LOW">Baixa</option>
                                    <option value="NORMAL">Normal (padrão)</option>
                                    <option value="HIGH">Alta</option>
                                    <option value="URGENT">Urgente</option>
                                </select>
                                <p className="px-1 text-[10px] text-slate-600">A prioridade poderá ser revisada posteriormente por um responsável técnico.</p>
                            </div>
                        </div>
                    </FormSection>

                    <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} type="submit" disabled={submitting || (!!id && !editReady)}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 text-xs font-black uppercase tracking-wider text-white shadow-xl shadow-blue-600/20 transition hover:bg-blue-500 disabled:opacity-40">
                        {submitting ? <><RefreshCw className="animate-spin" size={18} /> Registrando...</> : <><Send size={18} /> {id ? 'Salvar alterações' : 'Registrar chamado'}</>}
                    </motion.button>
                </form>
            </div>

            <AnimatePresence>
                {showQuickCreate && (
                    <ModalPortal><motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <motion.form onSubmit={createApplicant} initial={{ opacity: 0, scale: 0.95, y: 15 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="w-full max-w-2xl rounded-3xl border border-white/10 bg-[#101622] p-6 shadow-2xl">
                            <header className="mb-5 flex items-center justify-between"><div><h2 className="text-sm font-black uppercase text-white">Cadastro rápido de servidor</h2><p className="mt-1 text-[11px] text-slate-500">O cadastro será vinculado à secretaria ativa.</p></div><button type="button" onClick={() => setShowQuickCreate(false)} className="rounded-xl p-2 text-slate-500 hover:bg-white/5 hover:text-white"><X size={18} /></button></header>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="space-y-1.5 md:col-span-2"><label className={labelClass}>Nome completo *</label><input className={fieldClass} required value={quickName} onChange={event => setQuickName(event.target.value)} /></div>
                                <div className="space-y-1.5"><label className={labelClass}>E-mail</label><input type="email" pattern={EMAIL_INPUT_PATTERN} className={fieldClass} value={quickEmail} onChange={event => setQuickEmail(event.target.value)} placeholder="servidor@prefeitura.gov.br" /></div>
                                <div className="space-y-1.5"><label className={labelClass}>Telefone</label><input className={fieldClass} value={quickPhone} onChange={event => setQuickPhone(formatPhone(event.target.value))} placeholder="(99)99999-9999" /></div>
                                <div className="space-y-1.5"><label className={labelClass}>Unidade de lotação *</label><select className={fieldClass} required value={quickUnitId} onChange={event => { setQuickUnitId(event.target.value); setQuickLocationId(''); }}><option value="">Selecione</option>{units.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                                <div className="space-y-1.5"><label className={labelClass}>Local de lotação *</label><select className={fieldClass} required value={quickLocationId} disabled={!quickUnitId} onChange={event => setQuickLocationId(event.target.value)}><option value="">Selecione</option>{quickLocations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setShowQuickCreate(false)} className="rounded-xl px-4 py-2.5 text-xs font-bold text-slate-400 hover:bg-white/5">Cancelar</button><button type="submit" disabled={creatingApplicant} className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-black text-white disabled:opacity-40">{creatingApplicant ? <RefreshCw className="animate-spin" size={15} /> : <Plus size={15} />} Cadastrar e selecionar</button></div>
                        </motion.form>
                    </motion.div></ModalPortal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CallPage;
